import React from 'react';

interface PaginationProps {
  currentPage: number;
  totalPages: number;
  /** Сколько элементов всего (для подписи «N–M из K»). Опционально. */
  totalItems?: number;
  /** Сколько элементов на странице (для подписи). Опционально. */
  pageSize?: number;
  onPageChange: (page: number) => void;
}

/**
 * Компактная пагинация в стиле всех наших табличных страниц.
 * Показывает диапазон (если переданы totalItems и pageSize), стрелки
 * назад/вперёд и номер текущей страницы.
 */
const Pagination: React.FC<PaginationProps> = ({
  currentPage,
  totalPages,
  totalItems,
  pageSize,
  onPageChange,
}) => {
  if (totalPages <= 1) {
    // Одна страница — пагинацию не показываем, а низ просто пустой.
    return null;
  }

  const rangeStart =
    totalItems != null && pageSize != null
      ? (currentPage - 1) * pageSize + 1
      : null;
  const rangeEnd =
    totalItems != null && pageSize != null
      ? Math.min(currentPage * pageSize, totalItems)
      : null;

  const goPrev = () => onPageChange(Math.max(1, currentPage - 1));
  const goNext = () => onPageChange(Math.min(totalPages, currentPage + 1));

  const btn =
    'flex items-center justify-center w-8 h-8 rounded-md border border-gray-200 text-gray-600 hover:bg-gray-50 hover:text-[#2d2c2c] transition-colors disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-white disabled:hover:text-gray-600';

  return (
    <div className="flex items-center justify-between gap-3 mt-4 text-sm">
      <div className="text-gray-500">
        {rangeStart != null && rangeEnd != null && totalItems != null ? (
          <span>
            Показано {rangeStart}–{rangeEnd} из {totalItems}
          </span>
        ) : (
          <span>
            Страница {currentPage} из {totalPages}
          </span>
        )}
      </div>

      <div className="flex items-center gap-1">
        <button
          type="button"
          onClick={goPrev}
          disabled={currentPage === 1}
          className={btn}
          aria-label="Предыдущая страница"
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
            <path
              d="M15 18L9 12L15 6"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
        <span className="px-3 text-sm font-medium text-[#2d2c2c]">
          {currentPage} / {totalPages}
        </span>
        <button
          type="button"
          onClick={goNext}
          disabled={currentPage === totalPages}
          className={btn}
          aria-label="Следующая страница"
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
            <path
              d="M9 6L15 12L9 18"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
      </div>
    </div>
  );
};

export default Pagination;
